import { h } from 'preact';
import { useState, useEffect, useMemo } from 'preact/hooks';
import { api } from '../state/auth.js';
import { route } from 'preact-router';
import { translateError } from '../i18n.js';
import { useAuth } from '../hooks/useAuth.js';
import Modal from '../components/shared/Modal.jsx';
import Button from '../components/shared/Button.jsx';
import { useGoalsFor, GoalChips } from '../components/shared/GoalChips.jsx';

const MONTH_LABELS = {
  '01': 'Jan', '02': 'Feb', '03': 'Mrt',
  '04': 'Apr', '05': 'Mei', '06': 'Jun',
  '07': 'Jul', '08': 'Aug', '09': 'Sep',
  '10': 'Okt', '11': 'Nov', '12': 'Dec',
};

const MONTH_LABELS_FULL = {
  '01': 'Januari', '02': 'Februari', '03': 'Maart',
  '04': 'April',   '05': 'Mei',      '06': 'Juni',
  '07': 'Juli',    '08': 'Augustus', '09': 'September',
  '10': 'Oktober', '11': 'November', '12': 'December',
};

// Compute planning_period from planning_month + season start_date (mirrors backend logic)
function computePeriod(planning_month, start_date) {
  if (!planning_month || planning_month === 'continue') return 'continue';
  if (!start_date) return null;
  const startYear  = parseInt(start_date.slice(0, 4), 10);
  const startMonth = parseInt(start_date.slice(5, 7), 10);
  const month      = parseInt(planning_month, 10);
  const year       = month >= startMonth ? startYear : startYear + 1;
  return `${year}-${planning_month}`;
}

// Generate ordered list of planning_month values for a season (start_month first)
function seasonMonths(start_date) {
  if (!start_date) return Object.keys(MONTH_LABELS);
  const startMonth = parseInt(start_date.slice(5, 7), 10);
  const months = [];
  for (let i = 0; i < 12; i++) {
    const m = ((startMonth - 1 + i) % 12) + 1;
    months.push(String(m).padStart(2, '0'));
  }
  return months;
}

function currentYearMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

const ROLE_COLORS = {
  O: { bg: '#fef3c7', color: '#92400e', label: 'Opdrachtgevend' },
  U: { bg: '#dbeafe', color: '#1e40af', label: 'Uitvoerend' },
  a: { bg: '#f0fdf4', color: '#166534', label: 'Assisterend' },
};

const STATUS_CFG = {
  open:   { label: 'Te doen',  bg: '#f3f4f6', color: '#6b7280', next: 'bezig' },
  bezig:  { label: 'Bezig',    bg: '#dbeafe', color: '#1d4ed8', next: 'gedaan' },
  gedaan: { label: 'Gedaan',   bg: '#dcfce7', color: '#15803d', next: 'open' },
};

function RoleBadge({ role }) {
  const cfg = ROLE_COLORS[role] || {};
  return (
    <span style={`display:inline-block;padding:1px 7px;border-radius:10px;font-size:11px;font-weight:700;background:${cfg.bg};color:${cfg.color}`}
          title={cfg.label}>
      {role}
    </span>
  );
}

function StatusBadge({ status }) {
  const cfg = STATUS_CFG[status] || STATUS_CFG.open;
  return (
    <span style={`display:inline-flex;align-items:center;gap:4px;padding:3px 10px;border-radius:12px;font-size:12px;font-weight:600;background:${cfg.bg};color:${cfg.color}`}>
      {status === 'gedaan' ? '✓ ' : status === 'bezig' ? '⟳ ' : '○ '}
      {cfg.label}
    </span>
  );
}

// Nog geen seizoen: de jaarplanning loopt per seizoen. Wie de club beheert, maakt het meteen
// aan via de inrichtingsroute, waar het seizoen al vooraf is ingevuld. Why alleen die:
// Seizoenbeheer toont het starten van een seizoen ook alleen aan wie de club beheert.
function NoSeason({ canStart }) {
  return (
    <div class="card" style="padding:40px;text-align:center">
      <div style="font-size:28px;margin-bottom:8px">🗓️</div>
      <div style="font-weight:700;margin-bottom:6px">Er is nog geen seizoen</div>
      <div class="text-sm text-muted" style="max-width:420px;margin:0 auto">
        {canStart
          ? 'De jaarplanning loopt per maand door het seizoen. Maak eerst het seizoen aan, dan kun je hier activiteiten plannen.'
          : 'Zodra het bestuur het seizoen heeft aangemaakt, zie je hier de jaarplanning.'}
      </div>
      {canStart && (
        <div style="margin-top:16px">
          <Button onClick={() => route('/inrichten/seizoen')}>Seizoen aanmaken</Button>
        </div>
      )}
    </div>
  );
}

function SkeletonRow() {
  return (
    <div style="padding:12px 16px;border-bottom:1px solid var(--color-border);display:flex;gap:12px;align-items:center">
      <div class="skeleton" style="width:40%;height:14px" />
      <div class="skeleton" style="width:15%;height:14px" />
      <div class="skeleton" style="width:70px;height:22px;border-radius:12px" />
    </div>
  );
}

function AssignmentPicker({ sections, commissions, assignments, onChange }) {
  const ROLES = ['O', 'U', 'a'];

  function toggleRole(type, id, role) {
    const existing = assignments.find(a => a.entity_type === type && a.entity_id === id);
    if (existing && existing.role === role) {
      onChange(assignments.filter(a => !(a.entity_type === type && a.entity_id === id)));
    } else if (existing) {
      onChange(assignments.map(a =>
        a.entity_type === type && a.entity_id === id ? { ...a, role } : a
      ));
    } else {
      onChange([...assignments, { entity_type: type, entity_id: id, role }]);
    }
  }

  function getRole(type, id) {
    return assignments.find(a => a.entity_type === type && a.entity_id === id)?.role || null;
  }

  function renderGroup(label, items, type) {
    if (!items.length) return null;
    return (
      <div style="margin-bottom:16px">
        <div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:var(--color-text-muted);margin-bottom:8px">{label}</div>
        {items.map(item => {
          const current = getRole(type, item.id);
          return (
            <div key={item.id} style="display:flex;align-items:center;gap:8px;margin-bottom:6px">
              <span style="flex:1;font-size:13px">{item.name}</span>
              {ROLES.map(r => (
                <button key={r} type="button"
                  onClick={() => toggleRole(type, item.id, r)}
                  style={`padding:2px 10px;border-radius:10px;font-size:11px;font-weight:600;cursor:pointer;border:1.5px solid ${current === r ? ROLE_COLORS[r].color : 'var(--color-border)'};background:${current === r ? ROLE_COLORS[r].bg : 'transparent'};color:${current === r ? ROLE_COLORS[r].color : 'var(--color-text-muted)'}`}
                  title={ROLE_COLORS[r].label}>
                  {r}
                </button>
              ))}
              {current && (
                <button type="button" onClick={() => toggleRole(type, item.id, current)}
                  style="font-size:11px;color:var(--color-text-muted);background:none;border:none;cursor:pointer">✕</button>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div>
      {renderGroup('Secties', sections, 'section')}
      {renderGroup('Commissies', commissions, 'commission')}
      {!sections.length && !commissions.length && (
        <p style="color:var(--color-text-muted);font-size:13px">Geen secties of commissies gevonden.</p>
      )}
    </div>
  );
}

export default function JaarplanningView() {
  const auth    = useAuth();
  const isAdmin   = auth.isPlatformAdmin || auth.hasPermission('templates', 'create');
  const canPrep   = isAdmin || auth.hasPermission('jaarplanning', 'prep');

  const [items, setItems]   = useState([]);
  const goalMap = useGoalsFor('activity', items.map(i => i.id));
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);
  const [mineOnly, setMineOnly] = useState(true);
  const [claiming, setClaiming] = useState({});
  const [claimToast, setClaimToast] = useState(null);
  const [editingNote, setEditingNote] = useState(null);
  const [noteValues, setNoteValues]   = useState({});

  const [seasons, setSeasons]             = useState([]);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(currentYearMonth());

  // Admin modal state
  const [meta, setMeta]         = useState({ sections: [], commissions: [] });
  const [editModal, setEditModal] = useState(null);
  const [saving, setSaving]       = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Import starter set
  const [starterSets, setStarterSets]   = useState([]);
  const [importModal, setImportModal]   = useState(false);
  const [selectedSetId, setSelectedSetId] = useState(null);
  const [importing, setImporting]         = useState(false);

  const seasonId    = selectedSeason?.id || null;
  const startDate   = selectedSeason?.start_date || null;
  const seasonLabel = selectedSeason?.name || '';

  const fetchItems = (sid) => {
    if (!sid) return;
    setLoading(true);
    api(`/api/jaarplanning?season_id=${sid}`)
      .then(data => { setItems(data); setLoading(false); })
      .catch(() => { setError('Kon jaarplanning niet laden.'); setLoading(false); });
  };

  useEffect(() => {
    api('/api/seasons').then(data => {
      const list = Array.isArray(data) ? data : [];
      // Prep users and admins see all non-archived seasons; members only see the active one
      const visible = canPrep
        ? list.filter(s => !s.is_archived)
        : list.filter(s => s.is_active);
      setSeasons(visible);
      const active = visible.find(s => s.is_active) || visible[0] || null;
      setSelectedSeason(active);
      // Why: zonder seizoen haalt fetchItems niets op; anders bleef de pagina eeuwig laden
      if (!active) setLoading(false);
    }).catch(() => { setError('Kon de seizoenen niet laden.'); setLoading(false); });
  }, [canPrep]);

  useEffect(() => { fetchItems(seasonId); }, [seasonId]);

  useEffect(() => {
    if (canPrep) {
      api('/api/jaarplanning/meta').then(setMeta).catch(() => {});
      api('/api/jaarplanning/starter-sets').then(data => {
        setStarterSets(Array.isArray(data) ? data : []);
      }).catch(() => {});
    }
  }, [canPrep]);

  // When season changes, reset selected period to current month
  useEffect(() => { setSelectedPeriod(currentYearMonth()); }, [seasonId]);

  async function saveNote(item) {
    const note = noteValues[item.id] ?? item.instance?.notes ?? '';
    setEditingNote(null);
    try {
      const result = await api(`/api/jaarplanning/instances/${item.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ season_id: seasonId, notes: note }),
      });
      setItems(prev => prev.map(i =>
        i.id === item.id
          ? { ...i, instance: { ...(i.instance || {}), instance_id: result.instance_id, notes: note } }
          : i
      ));
    } catch { /* ignore */ }
  }


  const allPeriods = useMemo(() => {
    const visible = mineOnly && !isAdmin ? items.filter(i => i.user_role) : items;
    const set = new Set(visible.map(i => i.planning_period || 'continue'));
    return [...set].sort((a, b) => {
      if (a === 'continue') return 1;
      if (b === 'continue') return -1;
      return a.localeCompare(b);
    });
  }, [items, mineOnly, isAdmin]);

  const periodStatsMap = useMemo(() => {
    const visible = mineOnly && !isAdmin ? items.filter(i => i.user_role) : items;
    const map = {};
    for (const item of visible) {
      const p = item.planning_period || 'continue';
      if (!map[p]) map[p] = { total: 0, done: 0 };
      map[p].total++;
      if (item.instance?.status === 'gedaan') map[p].done++;
    }
    return map;
  }, [items, mineOnly, isAdmin]);

  async function claimTask(item) {
    setClaiming(c => ({ ...c, [item.id]: true }));
    try {
      const result = await api(`/api/jaarplanning/instances/${item.id}/claim`, {
        method: 'POST',
        body: JSON.stringify({ season_id: seasonId }),
      });
      setItems(prev => prev.map(i => i.id === item.id ? {
        ...i,
        can_claim: false,
        instance: {
          ...(i.instance || {}),
          status: result.status,
          claimed_by_name: result.claimed_by_name,
          claimed_at: result.claimed_at,
          is_claimant: true,
          task_id: result.task_id,
        },
      } : i));
      setClaimToast('Taak aangenomen! Je vindt hem in het takenoverzicht.');
      setTimeout(() => setClaimToast(null), 5000);
    } catch (e) {
      alert(translateError(e.code, e.detail) || 'Aannemen is niet gelukt.');
    }
    setClaiming(c => ({ ...c, [item.id]: false }));
  }

  async function unclaimTask(item) {
    setClaiming(c => ({ ...c, [item.id]: true }));
    try {
      await api(`/api/jaarplanning/instances/${item.id}/claim?season_id=${seasonId}`, { method: 'DELETE' });
      setItems(prev => prev.map(i => i.id === item.id ? {
        ...i,
        can_claim: true,
        instance: i.instance ? {
          ...i.instance,
          status: 'open',
          claimed_by_name: null,
          claimed_by_user_id: null,
          claimed_at: null,
          is_claimant: false,
          task_id: null,
        } : null,
      } : i));
    } catch (e) {
      // Why: a failed release used to vanish silently; the claim then looked stuck
      alert(translateError(e.code, e.detail) || 'Vrijgeven is niet gelukt.');
    }
    setClaiming(c => ({ ...c, [item.id]: false }));
  }

  const grouped = useMemo(() => {
    const visible = mineOnly && !isAdmin
      ? items.filter(i => i.user_role)
      : items;

    const filtered = visible.filter(i => {
      const p = i.planning_period || 'continue';
      return p === selectedPeriod || (selectedPeriod !== 'continue' && p === 'continue');
    });

    const map = {};
    for (const item of filtered) {
      const period = item.planning_period || 'continue';
      if (!map[period]) map[period] = [];
      map[period].push(item);
    }

    const sortedKeys = Object.keys(map).sort((a, b) => {
      if (a === 'continue') return 1;
      if (b === 'continue') return -1;
      return a.localeCompare(b);
    });

    return sortedKeys.map(key => ({ period: key, items: map[key] }));
  }, [items, mineOnly, isAdmin, selectedPeriod]);

  function periodStats(periodItems) {
    const done = periodItems.filter(i => i.instance?.status === 'gedaan').length;
    return { done, total: periodItems.length };
  }

  function periodLabel(period, full = false) {
    if (period === 'continue') return 'Doorlopend';
    const [y, month] = period.split('-');
    const label = full ? (MONTH_LABELS_FULL[month] || month) : (MONTH_LABELS[month] || month);
    return `${label} ${y}`;
  }

  function openEdit(item) {
    setEditModal({
      activity: {
        id: item.id,
        name: item.name,
        description: item.description || '',
        planning_month: item.planning_month || '',
      },
      assignments: [...(item.assignments || [])],
    });
    setSaveError(null);
  }

  async function saveEdit() {
    setSaving(true);
    setSaveError(null);
    try {
      let activityId = editModal.activity.id;

      if (!activityId) {
        const created = await api('/api/jaarplanning/activities', {
          method: 'POST',
          body: JSON.stringify({
            season_id: seasonId,
            name: editModal.activity.name,
            description: editModal.activity.description,
            planning_month: editModal.activity.planning_month,
          }),
        });
        activityId = created.id;
      } else {
        await api(`/api/jaarplanning/activities/${activityId}`, {
          method: 'PUT',
          body: JSON.stringify({
            season_id: seasonId,
            name: editModal.activity.name,
            description: editModal.activity.description,
            planning_month: editModal.activity.planning_month,
          }),
        });
      }

      await api(`/api/jaarplanning/assignments/${activityId}`, {
        method: 'PUT',
        body: JSON.stringify({ assignments: editModal.assignments }),
      });

      setEditModal(null);
      fetchItems(seasonId);
    } catch {
      setSaveError('Opslaan mislukt.');
    } finally {
      setSaving(false);
    }
  }

  async function importStarterSet() {
    if (!selectedSetId) return;
    setImporting(true);
    try {
      const result = await api(`/api/jaarplanning/starter-sets/${selectedSetId}/import`, {
        method: 'POST',
        body: JSON.stringify({ season_id: seasonId }),
      });
      setImportModal(false);
      setSelectedSetId(null);
      fetchItems(seasonId);
      const overgeslagen = result.skipped ? ` ${result.skipped} stonden er al.` : '';
      alert(`${result.imported} activiteiten geïmporteerd (${result.assign_imported} roltoewijzingen) voor ${result.season}.${overgeslagen}`);
    } catch {
      alert('Importeren mislukt. Probeer opnieuw.');
    } finally {
      setImporting(false);
    }
  }

  async function deleteActivity(item) {
    if (!confirm(`Activiteit "${item.name}" verwijderen? Dit kan niet ongedaan worden gemaakt.`)) return;
    try {
      await api(`/api/jaarplanning/activities/${item.id}`, { method: 'DELETE' });
      fetchItems(seasonId);
    } catch (e) {
      alert(translateError(e.code, e.detail) || 'Verwijderen is niet gelukt.');
    }
  }

  // Period options for the edit modal, ordered by season start month
  const periodOptions = useMemo(() => {
    const months = seasonMonths(startDate);
    return [
      ...months.map(m => {
        const period = computePeriod(m, startDate);
        return { value: m, label: period ? periodLabel(period) : `Maand ${m}` };
      }),
      { value: 'continue', label: 'Doorlopend' },
    ];
  }, [startDate]);

  return (
    <div>
      <style>{`
        .jp-row { display:grid; grid-template-columns:minmax(0,1fr) 260px 120px 100px; gap:12px; padding:11px 16px; border-bottom:1px solid var(--color-border); align-items:start; transition:background .12s; }
        .jp-header { display:grid; grid-template-columns:minmax(0,1fr) 260px 120px 100px; gap:12px; padding:8px 16px; background:var(--color-surface-alt,#f9fafb); border-bottom:1px solid var(--color-border); border-radius:8px 8px 0 0; }
        @media (max-width: 640px) {
          .jp-row { grid-template-columns:1fr; gap:8px; }
          .jp-header { display:none; }
          .jp-row .jp-commissies { font-size:11px; }
          .jp-row .jp-meta { display:flex; flex-direction:row; align-items:center; justify-content:space-between; gap:8px; flex-wrap:wrap; }
        }
      `}</style>

      {claimToast && (
        <div style="position:fixed;bottom:24px;left:50%;transform:translateX(-50%);background:#1d4ed8;color:#fff;padding:12px 20px;border-radius:10px;font-size:14px;font-weight:600;z-index:9999;box-shadow:0 4px 16px rgba(0,0,0,.18);display:flex;align-items:center;gap:8px">
          ✓ {claimToast}
        </div>
      )}

      <div style="display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:8px;margin-bottom:20px">
        <div style="display:flex;align-items:center;gap:12px">
          <h1 style="font-size:22px;font-weight:700;margin:0">Jaarplanning</h1>
          {/* Season selector: prep users and admins see all non-archived seasons */}
          {canPrep && seasons.length > 1 && (
            <select
              class="form-input"
              value={selectedSeason?.id || ''}
              onChange={e => {
                const s = seasons.find(s => String(s.id) === e.target.value);
                setSelectedSeason(s || null);
              }}
              style="height:32px;font-size:13px;padding:0 8px;min-width:160px"
            >
              {seasons.map(s => (
                <option key={s.id} value={s.id}>
                  {s.name}{s.is_active ? ' ✓' : ''}
                </option>
              ))}
            </select>
          )}
          {selectedSeason && !selectedSeason.is_active && (
            <span style="font-size:11px;background:#fef3c7;color:#92400e;padding:2px 8px;border-radius:10px;font-weight:600">
              Voorbereiding
            </span>
          )}
        </div>
        <div style="display:flex;gap:8px;align-items:center">
          {!isAdmin && (
            <label style="display:flex;align-items:center;gap:6px;font-size:13px;cursor:pointer">
              <input type="checkbox" checked={mineOnly} onChange={e => setMineOnly(e.target.checked)} />
              Alleen mijn taken
            </label>
          )}
          {canPrep && (
            <div style="display:flex;gap:8px">
              {starterSets.length > 0 && seasonId && (
                <Button size="sm" variant="secondary" onClick={() => { setImportModal(true); setSelectedSetId(starterSets[0]?.id || null); }}>
                  ⬇ Startersset importeren
                </Button>
              )}
              {isAdmin && seasonId && (
                <Button size="sm" onClick={() => { setEditModal({ activity: { id: null, name: '', description: '', planning_month: '' }, assignments: [] }); setSaveError(null); }}>
                  + Activiteit toevoegen
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Month pill navigation */}
      {!loading && allPeriods.length > 0 && (() => {
        const now = currentYearMonth();
        const monthPeriods = allPeriods.filter(p => p !== 'continue');
        const hasContinu = allPeriods.includes('continue');
        return (
          <div style="margin-bottom:20px">
            <div style="display:flex;flex-wrap:wrap;gap:6px;align-items:center">
              {monthPeriods.map(p => {
                const stats   = periodStatsMap[p] || { total: 0, done: 0 };
                const isPast  = p < now;
                const isCurrent  = p === now;
                const isSelected = p === selectedPeriod;
                const allDone    = stats.total > 0 && stats.done === stats.total;
                const someDone   = stats.done > 0 && !allDone;

                return (
                  <button key={p} onClick={() => setSelectedPeriod(p)}
                    style={`display:flex;align-items:center;gap:5px;padding:5px 12px;border-radius:20px;font-size:12px;font-weight:${isCurrent || isSelected ? '700' : '500'};cursor:pointer;border:2px solid ${isSelected ? 'var(--color-primary,#2563eb)' : isCurrent ? 'var(--color-primary,#2563eb)' : 'var(--color-border)'};background:${isSelected ? 'var(--color-primary,#2563eb)' : 'transparent'};color:${isSelected ? '#fff' : isPast ? 'var(--color-text-muted)' : 'var(--color-text)'};opacity:${isPast && !isSelected ? '.65' : '1'};transition:all .15s`}>
                    {isCurrent && !isSelected && <span style="width:6px;height:6px;border-radius:50%;background:var(--color-primary,#2563eb);flex-shrink:0" />}
                    {allDone && <span style={`width:6px;height:6px;border-radius:50%;background:${isSelected ? '#fff' : '#16a34a'};flex-shrink:0`} />}
                    {someDone && !allDone && <span style={`width:6px;height:6px;border-radius:50%;background:${isSelected ? '#fff' : '#f59e0b'};flex-shrink:0`} />}
                    {periodLabel(p)}
                  </button>
                );
              })}
              {hasContinu && (
                <button onClick={() => setSelectedPeriod('continue')}
                  style={`display:flex;align-items:center;gap:5px;padding:5px 12px;border-radius:20px;font-size:12px;font-weight:500;cursor:pointer;border:2px dashed ${'continue' === selectedPeriod ? 'var(--color-primary,#2563eb)' : 'var(--color-border)'};background:${'continue' === selectedPeriod ? 'var(--color-primary,#2563eb)' : 'transparent'};color:${'continue' === selectedPeriod ? '#fff' : 'var(--color-text-muted)'};transition:all .15s`}>
                  ∞ Doorlopend
                </button>
              )}
            </div>
          </div>
        );
      })()}

      {error && <div class="alert alert-error">{error}</div>}

      {loading ? (
        <div>
          {[0, 1, 2].map(g => (
            <div key={g} class="card mb-4">
              <div style="padding:12px 16px;border-bottom:1px solid var(--color-border)">
                <div class="skeleton" style="width:120px;height:18px" />
              </div>
              {[0, 1, 2].map(r => <SkeletonRow key={r} />)}
            </div>
          ))}
        </div>
      ) : !seasonId && !error ? (
        <NoSeason canStart={auth.hasPermission('settings', 'update')} />
      ) : !seasonId ? (
        null
      ) : grouped.length === 0 ? (
        <div class="card" style="padding:40px;text-align:center;color:var(--color-text-muted)">
          {mineOnly && !isAdmin ? 'Je bent niet gekoppeld aan taken in de jaarplanning.' : 'Er zijn nog geen taken in de jaarplanning.'}
        </div>
      ) : (
        grouped.map(({ period, items: periodItems }) => {
          const { done, total } = periodStats(periodItems);
          return (
            <div key={period} class="card mb-4">
              <div style="padding:10px 16px;border-bottom:2px solid var(--color-border);display:flex;align-items:center;gap:10px">
                <span style="font-size:15px;font-weight:700">{periodLabel(period, true)}</span>
                <span style="font-size:12px;color:var(--color-text-muted);background:var(--color-surface-alt,#f3f4f6);padding:1px 8px;border-radius:10px">{total} taken</span>
                {done > 0 && (
                  <span style="font-size:12px;color:#15803d;background:#dcfce7;padding:1px 8px;border-radius:10px;font-weight:600">{done}/{total} gedaan</span>
                )}
              </div>

              <div class="jp-header" style="font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.04em;color:var(--color-text-muted)">
                <span>Taak</span>
                <span>Commissies</span>
                <span>Jouw rol</span>
                <span>Status</span>
              </div>

              {periodItems.map(item => {
                const status        = item.instance?.status || 'open';
                const isClaiming    = claiming[item.id];
                const isClaimant    = item.instance?.is_claimant;
                const claimedByName = item.instance?.claimed_by_name;
                const currentNote   = noteValues[item.id] ?? item.instance?.notes ?? '';
                const isEditingNote = editingNote === item.id;
                return (
                  <div key={item.id}
                    class="jp-row"
                    style={status === 'gedaan' ? 'opacity:.7' : ''}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--color-surface-alt,#f9fafb)'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}>

                    <div>
                      <div style={`font-weight:500;font-size:13px;${status === 'gedaan' ? 'text-decoration:line-through;color:var(--color-text-muted)' : ''}`}>
                        {item.name}
                      </div>
                      {item.description && <div style="font-size:12px;color:var(--color-text-muted);margin-top:2px">{item.description}</div>}
                      <GoalChips goals={goalMap[item.id]} />

                      {isEditingNote ? (
                        <textarea
                          autoFocus
                          rows={3}
                          style="margin-top:6px;width:100%;box-sizing:border-box;font-size:12px;padding:5px 8px;border:1px solid var(--color-border);border-radius:6px;resize:vertical;font-family:inherit;background:var(--color-surface)"
                          value={currentNote}
                          onInput={e => setNoteValues(n => ({ ...n, [item.id]: e.target.value }))}
                          onBlur={() => saveNote(item)}
                          onKeyDown={e => { if (e.key === 'Escape') setEditingNote(null); }}
                          placeholder="Notitie toevoegen…"
                        />
                      ) : item.instance?.notes ? (
                        <div style="margin-top:5px;display:flex;align-items:flex-start;gap:4px;cursor:pointer"
                          onClick={() => { setNoteValues(n => ({ ...n, [item.id]: item.instance.notes })); setEditingNote(item.id); }}>
                          <span style="font-size:12px">📝</span>
                          <span style="font-size:12px;color:var(--color-text-muted);font-style:italic;word-break:break-word">{item.instance.notes}</span>
                        </div>
                      ) : (
                        <button onClick={() => { setNoteValues(n => ({ ...n, [item.id]: '' })); setEditingNote(item.id); }}
                          style="margin-top:4px;font-size:11px;color:var(--color-text-muted);background:none;border:none;cursor:pointer;padding:0;display:block">
                          + notitie
                        </button>
                      )}

                      {isAdmin && (
                        <span style="display:flex;gap:8px;margin-top:2px">
                          <button onClick={() => openEdit(item)}
                            style="font-size:11px;color:var(--color-text-muted);background:none;border:none;cursor:pointer;padding:0">
                            ✎ bewerk
                          </button>
                          <button onClick={() => deleteActivity(item)}
                            style="font-size:11px;color:#ef4444;background:none;border:none;cursor:pointer;padding:0">
                            ✕ verwijder
                          </button>
                        </span>
                      )}
                    </div>

                    <div class="jp-commissies" style="font-size:11px;display:flex;flex-direction:column;gap:3px;align-items:flex-start">
                      {item.assignments.map(a => (
                        <span key={`${a.entity_type}:${a.entity_id}`}
                          style="display:flex;align-items:center;gap:4px;background:var(--color-surface-alt,#f3f4f6);border-radius:6px;padding:2px 6px;white-space:normal">
                          <span style="color:var(--color-text-muted);flex-shrink:0">{a.entity_type === 'section' ? '🏢' : '📋'}</span>
                          <span>{a.entity_name}</span>
                          <RoleBadge role={a.role} />
                        </span>
                      ))}
                      {item.assignments.length === 0 && <span style="color:var(--color-text-muted)">—</span>}
                    </div>

                    <div class="jp-meta" style="display:flex;flex-direction:column;gap:4px;align-items:flex-start">
                      {claimedByName ? (
                        <>
                          <span style={`font-size:12px;font-weight:600;color:${isClaimant ? 'var(--color-primary,#2563eb)' : 'var(--color-text)'}`}>
                            {isClaimant ? '★ Eigenaar' : claimedByName}
                          </span>
                          {isClaimant && (
                            <div style="display:flex;gap:8px;align-items:center">
                              {item.instance?.task_id && (
                                <a
                                  href={`/tasks/${item.instance.task_id}`}
                                  style="font-size:11px;color:var(--color-primary,#2563eb);text-decoration:none;font-weight:600"
                                >
                                  → Naar taak
                                </a>
                              )}
                              <button onClick={() => unclaimTask(item)} disabled={isClaiming}
                                style="font-size:11px;color:var(--color-text-muted);background:none;border:none;cursor:pointer;padding:0;text-decoration:underline">
                                {isClaiming ? '…' : 'Loslaten'}
                              </button>
                            </div>
                          )}
                          {isAdmin && !isClaimant && (
                            <button onClick={() => unclaimTask(item)} disabled={isClaiming}
                              style="font-size:11px;color:var(--color-text-muted);background:none;border:none;cursor:pointer;padding:0;text-decoration:underline">
                              {isClaiming ? '…' : '✕ Ontkoppel'}
                            </button>
                          )}
                        </>
                      ) : item.can_claim ? (
                        <button onClick={() => claimTask(item)} disabled={isClaiming}
                          style={`padding:3px 10px;border-radius:10px;font-size:12px;font-weight:600;border:1.5px solid var(--color-primary,#2563eb);background:transparent;color:var(--color-primary,#2563eb);cursor:pointer;white-space:nowrap;opacity:${isClaiming ? .5 : 1}`}>
                          {isClaiming ? '…' : 'Neem aan'}
                        </button>
                      ) : (
                        <span style="color:var(--color-text-muted);font-size:12px">—</span>
                      )}
                    </div>

                    <div>
                      <StatusBadge status={status} />
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })
      )}

      {/* Import starter set modal */}
      {importModal && (
        <Modal isOpen={true} title="Startersset importeren" onClose={() => setImportModal(false)}>
          <div style="min-width:400px;max-width:480px">
            <p style="font-size:13px;color:var(--color-text-muted);margin-bottom:16px">
              Kies een startersset om alle activiteiten te importeren in de jaarplanning voor <strong>{seasonLabel}</strong>.<br />
              Bestaande activiteiten blijven staan — er worden alleen nieuwe toegevoegd.
            </p>
            <div style="margin-bottom:16px">
              <label style="font-size:12px;font-weight:600;display:block;margin-bottom:4px">Startersset</label>
              <select class="form-input" style="width:100%;box-sizing:border-box"
                value={selectedSetId || ''}
                onChange={e => setSelectedSetId(parseInt(e.target.value))}>
                {starterSets.map(s => (
                  <option key={s.id} value={s.id}>{s.name}{s.sport_type ? ` — ${s.sport_type}` : ''} ({s.activities?.length || 0} activiteiten)</option>
                ))}
              </select>
            </div>
            {selectedSetId && (() => {
              const set = starterSets.find(s => s.id === selectedSetId);
              return set?.activities?.length > 0 ? (
                <div style="margin-bottom:16px;max-height:200px;overflow-y:auto;border:1px solid var(--color-border);border-radius:6px">
                  {set.activities.map(a => (
                    <div key={a.id} style="padding:6px 12px;border-bottom:1px solid var(--color-border);font-size:12px;display:flex;gap:8px;align-items:center">
                      <span style="background:var(--color-surface-alt,#f3f4f6);padding:1px 6px;border-radius:6px;font-size:11px;white-space:nowrap">
                        {a.planning_month === 'continue' ? 'Doorlopend' : (() => {
                          const p = computePeriod(a.planning_month, startDate);
                          return p ? periodLabel(p) : `Maand ${a.planning_month}`;
                        })()}
                      </span>
                      <span>{a.name}</span>
                    </div>
                  ))}
                </div>
              ) : null;
            })()}
            <div style="display:flex;gap:8px;justify-content:flex-end">
              <Button variant="secondary" onClick={() => setImportModal(false)}>Annuleren</Button>
              <Button onClick={importStarterSet} disabled={importing || !selectedSetId}>
                {importing ? 'Importeren…' : 'Importeren'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Admin edit modal */}
      {editModal && (
        <Modal isOpen={true}
          title={editModal.activity.id ? 'Bewerk activiteit' : 'Activiteit toevoegen'}
          onClose={() => setEditModal(null)}>
          <div style="min-width:460px;max-width:560px">

            <div style="margin-bottom:16px">
              <label style="font-size:12px;font-weight:600;display:block;margin-bottom:4px">Naam *</label>
              <input
                type="text"
                class="form-input"
                style="width:100%;box-sizing:border-box"
                placeholder="Bijv. Seizoensopening organiseren"
                value={editModal.activity.name}
                onInput={e => setEditModal(m => ({ ...m, activity: { ...m.activity, name: e.target.value } }))}
              />
            </div>

            <div style="margin-bottom:16px">
              <label style="font-size:12px;font-weight:600;display:block;margin-bottom:4px">Omschrijving</label>
              <input
                type="text"
                class="form-input"
                style="width:100%;box-sizing:border-box"
                placeholder="Optionele toelichting"
                value={editModal.activity.description || ''}
                onInput={e => setEditModal(m => ({ ...m, activity: { ...m.activity, description: e.target.value } }))}
              />
            </div>

            <div style="margin-bottom:16px">
              <label style="font-size:12px;font-weight:600;display:block;margin-bottom:4px">Planningsmaand *</label>
              <select
                class="form-input"
                style="width:100%;box-sizing:border-box"
                value={editModal.activity.planning_month || ''}
                onChange={e => setEditModal(m => ({ ...m, activity: { ...m.activity, planning_month: e.target.value } }))}>
                <option value="">— Kies maand —</option>
                {periodOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div style="margin-bottom:16px">
              <label style="font-size:12px;font-weight:600;display:block;margin-bottom:8px">
                Rolverdeling <span style="font-weight:400;color:var(--color-text-muted)">(O = Opdrachtgevend, U = Uitvoerend, a = assisterend)</span>
              </label>
              <AssignmentPicker
                sections={meta.sections}
                commissions={meta.commissions}
                assignments={editModal.assignments}
                onChange={a => setEditModal(m => ({ ...m, assignments: a }))}
              />
            </div>

            {saveError && <div class="alert alert-error" style="margin-bottom:12px">{saveError}</div>}

            <div style="display:flex;gap:8px;justify-content:flex-end">
              <Button variant="secondary" onClick={() => setEditModal(null)}>Annuleren</Button>
              <Button onClick={saveEdit} disabled={saving || !editModal.activity.name?.trim() || !editModal.activity.planning_month}>
                {saving ? 'Opslaan…' : 'Opslaan'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
